import { supabase } from '../services/supabase';

/**
 * Whether a signed-in user still owes the three-question onboarding.
 *
 * `needed`   — no row in howie_onboarding_reflections (never started, or
 *              abandoned mid-flow: the draft lives only in localStorage).
 * `resolved` — the row carries completed_at or skipped_at.
 *
 * The result is cached per user for the life of the page so the route gate
 * does not re-query on every navigation. OnboardingPage marks the user
 * resolved the moment it saves or skips, and sign-out clears the cache.
 */
export type OnboardingStatus = 'needed' | 'resolved';

const cache = new Map<string, OnboardingStatus>();

export async function fetchOnboardingStatus(userId: string): Promise<OnboardingStatus> {
  const cached = cache.get(userId);
  if (cached) return cached;

  const { data, error } = await supabase
    .from('howie_onboarding_reflections')
    .select('completed_at, skipped_at')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;

  const status: OnboardingStatus = data?.completed_at || data?.skipped_at ? 'resolved' : 'needed';
  cache.set(userId, status);
  return status;
}

/** What the cache already knows, without a query; the route gate seeds from this so a re-render never shows a spinner. */
export function peekOnboardingStatus(userId: string): OnboardingStatus | undefined {
  return cache.get(userId);
}

export function markOnboardingResolved(userId: string): void {
  cache.set(userId, 'resolved');
}

export function clearOnboardingStatusCache(): void {
  cache.clear();
}
